/**
 * SubscriptionData - 워크스페이스의 구독 정보 (수동 관리)
 * Stripe 의존성 없이 관리자가 직접 구독 기간/플랜을 설정
 * 
 * Firestore Path: workspaces/{id}/subscription/current
 */

import { WorkspaceTier } from './local/Types';

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trial' | 'expired';
export type PaymentMethod = 'invoice_transfer' | 'card' | 'manual';
export type DiscountType = 'percent' | 'fixed';

/**
 * 할인 정보
 */
export interface SubscriptionDiscount {
    type: DiscountType;         // 'percent' = 비율, 'fixed' = 고정금액
    value: number;              // percent: 0-100, fixed: 할인금액
    reason?: string;            // "初回契約プロモーション", "長期契約割引" 등
    validUntil?: Date | null;   // 할인 적용 종료일 (null = 무기한)
}

export interface SubscriptionData {
    planId: WorkspaceTier;     // 'BASIC', 'PREMIUM' etc.
    status: SubscriptionStatus;

    startDate: Date;           // 구독 시작일
    endDate?: Date | null;     // 구독 종료일 (계약 중이면 null, 갱신 안함 시 설정)

    paymentMethod: PaymentMethod; // 현재는 'invoice_transfer' (계좌이체)

    // Billing Info
    billingCycle?: 'monthly' | 'yearly';
    baseAmount?: number;        // 기본 요금 (Tier 정가)
    amount?: number;            // 최종 결제 금액 (할인 적용 후)
    currency?: 'JPY' | 'KRW' | 'USD';

    // 🆕 할인 정보
    discount?: SubscriptionDiscount | null;

    // Audit Trail
    lastUpdatedBy: string;     // Admin UID
    lastUpdatedAt: Date;
    notes?: string;            // 관리자 메모
}

export const defaultSubscriptionData = (): SubscriptionData => ({
    planId: 'FREE',
    status: 'active',
    startDate: new Date(),
    endDate: null,             // 기본값: 종료일 없음 (계약 중)
    paymentMethod: 'invoice_transfer',
    lastUpdatedBy: '',
    lastUpdatedAt: new Date(),
    discount: null,
});

/**
 * 할인 적용 후 최종 금액 계산
 */
export function calculateDiscountedPrice(baseAmount: number, discount?: SubscriptionDiscount | null): number {
    if (!discount || discount.value <= 0) return baseAmount;

    // 할인 유효기간 체크
    if (discount.validUntil && new Date() > discount.validUntil) {
        return baseAmount;
    }

    if (discount.type === 'percent') {
        return Math.round(baseAmount * (1 - discount.value / 100));
    } else {
        return Math.max(0, baseAmount - discount.value);
    }
}

/**
 * 할인율/금액 표시 문자열
 */
export function formatDiscount(discount?: SubscriptionDiscount | null): string {
    if (!discount) return '';

    if (discount.type === 'percent') {
        return `${discount.value}% OFF`;
    } else {
        return `¥${discount.value.toLocaleString()} OFF`;
    }
}

